'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { buildSearchIndex, searchNavigation } from '@/src/lib/navigation-search';
import type { ResolvedNavigationSection } from '@/src/lib/navigation';

export interface SearchPanelProps {
  isOpen: boolean;
  onClose: () => void;
  sections?: readonly ResolvedNavigationSection[];
}

export function SearchPanel({ isOpen, onClose, sections = [] }: SearchPanelProps) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const router = useRouter();
  const index = Array.from(new Map(buildSearchIndex(sections).map((item) => [item.href, item])).values());
  const results = searchNavigation(index, query, 10);

  function select(href: string) {
    router.push(href);
    onClose();
  }

  if (!isOpen) return null;

  return (
    <div role="dialog" aria-modal="true" aria-label="Globale Suche" data-tour="search-panel"
      className="fixed inset-0 z-[76] flex items-start justify-center bg-slate-900/55 px-4 pt-[12vh]"
      onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="w-full max-w-xl overflow-hidden rounded-[var(--k-radius-lg)] border border-[var(--k-color-border-default)] bg-[var(--k-color-surface)] shadow-[var(--k-shadow-lg)]">
        <input type="search" autoFocus value={query} aria-label="Suchfeld" data-tour="search-input"
          placeholder="Menüs und Funktionen suchen…"
          className="w-full border-b border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface)] px-4 py-3 text-sm focus:outline-none"
          onChange={(event) => { setQuery(event.target.value); setActiveIndex(0); }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') onClose();
            else if (event.key === 'ArrowDown') { event.preventDefault(); setActiveIndex((index) => Math.min(index + 1, Math.max(0, results.length - 1))); }
            else if (event.key === 'ArrowUp') { event.preventDefault(); setActiveIndex((index) => Math.max(index - 1, 0)); }
            else if (event.key === 'Enter' && results[activeIndex]) { event.preventDefault(); select(results[activeIndex].href); }
          }} />
        <ul role="listbox" aria-label="Suchergebnisse" className="max-h-[60vh] overflow-y-auto py-2">
          {query.trim().length < 2 ? <li className="px-4 py-6 text-sm text-[var(--k-color-text-muted)]">Mindestens zwei Zeichen eingeben. Die Suche zeigt freigegebene Menüs, keine Beleginhalte.</li>
            : results.length === 0 ? <li className="px-4 py-6 text-sm text-[var(--k-color-text-muted)]">Keine Ergebnisse für „{query}“.</li>
            : results.map((result, index) => (
              <li key={result.href} role="option" aria-selected={index === activeIndex}>
                <button type="button" onClick={() => select(result.href)} onMouseEnter={() => setActiveIndex(index)}
                  className={`block w-full px-4 py-3 text-left text-sm ${index === activeIndex ? 'bg-[var(--k-color-accent-soft)]' : ''}`}>
                  <span className="block font-semibold text-[var(--k-color-text-primary)]">{result.label}</span>
                  <span className="block text-xs text-[var(--k-color-text-muted)]">{result.section} · {result.group}</span>
                </button>
              </li>
            ))}
        </ul>
        <div className="border-t border-[var(--k-color-border-subtle)] px-4 py-2 text-xs text-[var(--k-color-text-muted)]">↑↓ navigieren · Enter öffnen · Esc schließen</div>
      </div>
    </div>
  );
}
